import { catalogReferenceClient } from "../catalogApprovals/catalogReferenceOptions";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import useCatalogAccess from "../useCatalogAccess";
import { readStoredUser } from "../../../utils/sessionUser";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "../CatalogCreationPendingNotice";
import InternalCodeSuggestions from "../InternalCodeSuggestions";
import { catalogRequestError, duplicateInternalCodeMessage, hasInternalCodeConflict } from "../internalCatalogCodes";
import { useCatalogTaxonomyText, useCatalogTaxonomyDialog } from "../catalogTaxonomyMessages";
import { useEffect, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";
import CategoryPicker, { categoryBranch } from "./CategoryPicker";

const buildEmptyForm = () => ({
  code: "",
  name: "",
  description: "",
  category_id: "",
});

export default function AddSubcategoryPopup({ isOpen, onClose, onAdded, existingRecords = [] }) {
  const t = useCatalogTaxonomyText();
  const { languageId } = usePmsTheme();
  const businessUnitId = useRef(readStoredUser()?.active_business_unit_id).current;
  const access = useCatalogAccess(businessUnitId);
  const permission = "purchasing.categories.create";
  const canCreate = access.can(permission);
  const [form, setForm] = useState(buildEmptyForm());
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState("");
  const [pendingRequest, setPendingRequest] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const dialogRef = useCatalogTaxonomyDialog(isOpen && canCreate && !pendingRequest, () => handleClose(), saving, categoryPickerOpen);

  useEffect(() => {
    if (!isOpen || !canCreate) return undefined;
    let cancelled = false;
    const stamp = access.capture();

    const loadCategories = async () => {
      try {
        const response = await catalogReferenceClient(axios, languageId).get("/api/purchasing/categories", access.requestConfig(permission));
        if (cancelled || !access.isCurrent(stamp, permission)) return;

        setCategories(response.data || []);
      } catch (requestError) {
        console.error("[SUBCATEGORIES][ADD][CATEGORIES][ERROR]", requestError);
        if (!cancelled && access.isCurrent(stamp, permission)) {
          setCategories([]);
          setError("We could not load the category list for this popup.");
        }
      } finally {
        if (!cancelled && access.isCurrent(stamp, permission)) setLoadingCategories(false);
      }
    };

    savingRef.current = false;
    setPendingRequest(null);
    setForm(buildEmptyForm());
    setSaving(false);
    setError("");
    setCategories([]);
    setLoadingCategories(true);
    setCategoryPickerOpen(false);
    loadCategories();

    return () => {
      cancelled = true;
    };
  }, [isOpen, canCreate, languageId, access, permission]);

  if (!isOpen || !canCreate) return null;
  if (pendingRequest) return <CatalogCreationPendingNotice request={pendingRequest} onClose={onClose} />;

  const handleClose = () => {
    if (savingRef.current) return;
    setForm(buildEmptyForm());
    setError("");
    setCategoryPickerOpen(false);
    onClose();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (savingRef.current || !access.can(permission)) return;
    if (hasInternalCodeConflict(form.code, existingRecords)) {
      setError(duplicateInternalCodeMessage(form.code));
      return;
    }
    if (!categories.some((category) => String(category.id) === form.category_id)) {
      setError("Select a category");
      dialogRef.current?.querySelector("#subcategory_category")?.focus();
      return;
    }
    const stamp = access.capture();
    savingRef.current = true;
    setSaving(true);
    setError("");

    try {
      const response = await axios.post("/api/purchasing/subcategories", { ...form, code: form.code.trim() }, access.requestConfig(permission));
      if (!access.isCurrent(stamp, permission)) return;
      if (isPendingCatalogCreation(response.data)) { setPendingRequest(response.data.request); setSaving(false); return; }
      if (typeof onAdded === "function") {
        await onAdded(response.data);
      }
      setForm(buildEmptyForm());
      onClose();
    } catch (requestError) {
      if (!access.isCurrent(stamp, permission)) return;
      console.error("[SUBCATEGORIES][ADD][ERROR]", requestError);
      savingRef.current = false;
      setError(catalogRequestError(requestError, "Error saving subcategory.", form.code));
      setSaving(false);
    }
  };

  const selectedCategory = categories.find((category) => String(category.id) === form.category_id);
  return (
    <div
      className="spx-modalBackdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >


      <div ref={dialogRef} className="spx-modal" role="dialog" aria-modal={categoryPickerOpen ? undefined : "true"}
        aria-hidden={categoryPickerOpen ? "true" : undefined} inert={categoryPickerOpen ? "" : undefined}
        aria-label={t("Add subcategory")}>
        <div className="spx-modalHeader">
          <div>
            <div className="spx-modalKicker">{t("Catalog Structure")}</div>
            <h3 className="spx-modalTitle">{t("Add Subcategory")}</h3>
            <p className="spx-modalSubtitle">{t("Select its category, then give this subcategory a code and name.")}</p>
          </div>

          <button type="button" className="spx-closeBtn" onClick={handleClose} disabled={saving} aria-label={t("Close add subcategory popup")}>
            x
          </button>
        </div>

        <form onSubmit={handleSubmit} className="spx-modalBody">
          <div className="spx-formGrid">
            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="subcategory_category">{t("Category")} </label>
              <button
                id="subcategory_category"
                name="category_id"
                type="button"
                onClick={() => setCategoryPickerOpen(true)}
                disabled={saving || loadingCategories}
                className="spx-input cp-trigger"
                aria-haspopup="dialog"
                aria-expanded={categoryPickerOpen}
              >
                <span>{loadingCategories ? t("Loading options...") : selectedCategory?.name || t("Select a category")}</span>
                {selectedCategory && <small>{[selectedCategory.code, categoryBranch(selectedCategory)].filter(Boolean).join(" · ")}</small>}
              </button>
            </div>
            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="subcategory_name">{t("Subcategory Name")} </label>
              <input
                id="subcategory_name"
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                disabled={saving}
                className="spx-input"
                placeholder={t("Example: Whiskies")}
                required
              />
            </div>

            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="subcategory_code">{t("Subcategory Code")} </label>
              <input
                id="subcategory_code"
                type="text"
                name="code"
                value={form.code}
                onChange={handleChange}
                disabled={saving}
                className="spx-input"
                placeholder={t("Example: SUB001")}
                required
              />
              <InternalCodeSuggestions name={form.name} value={form.code} resource="subcategories" disabled={saving}
                onSuggest={(code) => { setForm((previous) => ({ ...previous, code })); setError(""); }} />
            </div>

            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="subcategory_description">{t("Description")} </label>
              <textarea
                id="subcategory_description"
                name="description"
                value={form.description}
                onChange={handleChange}
                disabled={saving}
                className="spx-textarea"
                placeholder={t("Optional guidance for the team that classifies items inside this category.")}
              />
            </div>
          </div>

          {error ? <div className="spx-alert" role="alert">{t(error)}</div> : null}

          <div className="spx-modalFooter">
            <button type="button" className="spx-btn spx-btn--ghost" onClick={handleClose} disabled={saving}>{t("Cancel")} </button>
            <button type="submit" className="spx-btn spx-btn--primary" disabled={saving || loadingCategories}>
              {t(saving ? "Saving..." : "Save Subcategory")}
            </button>
          </div>
        </form>
      </div>
      {categoryPickerOpen && <CategoryPicker categories={categories} value={form.category_id}
        onChange={(category_id) => { setForm((previous) => ({ ...previous, category_id })); setError(""); }}
        onClose={() => setCategoryPickerOpen(false)} />}
    </div>
  );
}
